import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetslipConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { Observable, distinctUntilChanged, filter, pairwise } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { quickDepositSelector } from '../selector';

@Component({
    selector: 'bs-quick-deposit-message',
    templateUrl: 'quick-deposit-message.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class QuickDepositMessageComponent implements OnInit {
    showMessage = false;
    private clearTimeout: Nullable<() => void> = null;
    private amount: Nullable<number>;
    private readonly messages: any;

    quickDepositState$: Observable<any>;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        siteCore: Sitecore,
        private timerService: TimerService,
        private changeDetector: ChangeDetectorRef,
        private betslipConfig: BetslipConfig,
        private store: Store<IBetslipRootState>,
        private moneyService: MoneyService,
    ) {
        this.messages = siteCore.betslip.messages;
        this.quickDepositState$ = this.store.select(quickDepositSelector);
    }

    ngOnInit(): void {
        this.quickDepositState$
            .pipe(
                distinctUntilChanged((previous, current) => isEqual(previous, current)),
                pairwise(),
                filter(([previous, current]) => this.shouldAppear(previous, current) || this.shouldDisappear(previous, current)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([previous, current]) => {
                if (this.shouldAppear(previous, current)) {
                    this.restartNotification(previous.fundsAdded!.depositAmount);
                } else {
                    this.showMessage = false;
                }

                this.changeDetector.markForCheck();
            });
    }

    private shouldAppear(previous: any, current: any): boolean {
        return !!previous.fundsAdded?.success && !current.isOpen;
    }

    private shouldDisappear(previous: any, current: any): boolean {
        return !previous.isOpen && current.isOpen;
    }

    get messageContent(): string {
        return this.amount
            ? interpolateDoubleBracesString(this.messages.DepositSuccessful, {
                  amount: this.moneyService.transform(this.amount, true),
              })
            : '';
    }

    private restartNotification(amount: number): void {
        this.clearTimeout && this.clearTimeout();

        // amount is in cents in received message
        this.amount = amount / 100;
        this.showMessage = true;

        this.clearTimeout = this.timerService.setTimeout(() => {
            this.showMessage = false;
            this.clearTimeout = null;
            this.amount = this.clearTimeout;
            this.changeDetector.markForCheck();
        }, this.betslipConfig.depositSuccessfulVisibilityTimespan);
    }
}
